import { Component, OnInit, QueryList, ViewChildren, AfterViewInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormArray } from '@angular/forms';
import { Subject } from 'rxjs';
import { RecurrencePattern, RecurrenceDays, TissueCollectionType } from '../../shared/services/request.service';
import { ViewableRequest, RequestedSampling, SprayBoothStudyInstructions } from '../review-request/review-request.model';
import { ValidatorsService } from '../../shared/services/validators.service';
import {
  PlanningInformation,
  PlanningInformationType,
  PlanningInformationSubtype,
  PlanningTreatmentDetail,
  SamplingNeeds,
  PlanningTreatmentComment
} from '../../shared/services/planning.service';
import { RequestPlanningTreatmentComponent, PlanningTreatmentCopyArgs, SprayEventsCopyArgs } from './request-planning-treatment/request-planning-treatment.component';
import { RequestPlanningTreatmentSprayComponent } from './request-planning-treatment-spray/request-planning-treatment-spray.component';
import { Laboratory } from '../../shared/services/laboratory.service';
import { GrowthStage } from '../../shared/services/growth-stage.service';
import { TissueType } from '../../shared/services/tissue-type.service';
import { asEnumerable } from 'linq-es2015';
import { RequestType, RequestTypeService } from '../../shared/services/request-type.service';

@Component({
  selector: 'app-request-planning-modal',
  styleUrls: ['./request-planning-modal.component.css'],
  templateUrl: './request-planning-modal.component.html',
})
export class RequestPlanningModalComponent implements OnInit, AfterViewInit {
  @ViewChildren(RequestPlanningTreatmentComponent)
  planningTreatmentForms: QueryList<RequestPlanningTreatmentComponent>;
  @ViewChildren(RequestPlanningTreatmentSprayComponent) treatmentSprayComponents: QueryList<RequestPlanningTreatmentSprayComponent>;

  public planningInformation: PlanningInformation;
  public request: ViewableRequest;
  public isWheatRequest: boolean;
  public labs: Laboratory[] = [];
  public growthStages: GrowthStage[] = [];
  public tissueTypes: TissueType[] = [];
  public minPlantDate = new Date();
  public planningForm: FormGroup;
  public regulatoryStatuses: string;
  public existingTreatmentData: ExistingTreatmentData[] = [];
  public requestedSamplings: RequestedSampling[] = [];
  public requestTypes: RequestType[] = [];

  public get specificAoiRequired() { return this.planningForm.get('specificAoiRequired') };
  public get plannedAoiLocation() { return this.planningForm.get('plannedAoiLocation') };
  public get notes() { return this.planningForm.get("notes") };
  public get requestType() { return this.planningForm.get("requestType") };
  public get plannedPlantingDate() { return this.planningForm.get("plannedPlantingDate") };
  public get treatments(): FormArray { return <FormArray>this.planningForm.get("treatments") };
  public get requestedSamplingNotes(): FormArray { return <FormArray>this.planningForm.get("requestedSamplingNotes") };
  public get insectsAllowed() { return this.planningForm.get('insectsAllowed') };
  public get displayGrowthStage(): boolean {
    return this.requestedSamplings.some(r => r.growthStage !== null);
  }

  public get displayDaysAfterPlanting(): boolean {
    return this.requestedSamplings.some(r => r.daysAfterPlanting !== null);
  }

  public onClose: Subject<any> = new Subject<any>();
  public onSave: Subject<PlanningInformation> = new Subject<PlanningInformation>();
  public onPlanned: Subject<PlanningInformation> = new Subject<PlanningInformation>();

  constructor(
    private validatorsService: ValidatorsService,
    private requestTypeService: RequestTypeService,
    private formBuilder: FormBuilder) {
    this.planningForm = formBuilder.group({
      tagsOnly: formBuilder.control(false),
      insectsAllowed: formBuilder.control(true),
      specificAoiRequired: formBuilder.control(false),
      plannedAoiLocation: formBuilder.control(null),
      plannedPlantingDate: formBuilder.control(null, [Validators.required, this.validatorsService.mustBeOnOrAfterDate(this.minPlantDate), this.validatorsService.isValidDate]),
      notes: formBuilder.control(null),
      requestType: formBuilder.control(null, [Validators.required]),
      treatments: formBuilder.array([]),
      requestedSamplingNotes: formBuilder.array([])
    });
  }

  public ngOnInit() {
    var sortedTreatments = this.planningInformation.treatmentDetails.sort(t => t.treatmentNumber);

    sortedTreatments.forEach(st => {
      this.treatments.push(this.formBuilder.group({}));

      var currentRequestTreatment = this.request.treatments.find(t => t.treatmentNumber === st.treatmentNumber);
      this.existingTreatmentData.push({
        treatmentNumber: st.treatmentNumber,
        directSeedToPots: currentRequestTreatment.flatInstructions.directSeedToPots,
        flatStorageFacilityTypeId: st.flatStorageFacilityTypeId,
        flatStorageFacilityLocationId: st.flatStorageFacilityLocationId,
        automationPotStorageFacilityTypeId: st.automationPotStorageFacilityTypeId,
        automationPotStorageLocationId: st.automationPotStorageLocationId,
        staticPotStorageFacilityTypeId: st.staticPotStorageFacilityTypeId,
        staticPotStorageLocationId: st.staticPotStorageLocationId,
        flatIrrigationMethod: currentRequestTreatment.flatInstructions.irrigationMethod,
        flatFertigationMethod: currentRequestTreatment.flatInstructions.fertigationMethod,
        flatSubstrate: currentRequestTreatment.flatInstructions.substrate,
        potIrrigationMethod: currentRequestTreatment.pottingInstructions.irrigationMethod,
        potFertigationMethod: currentRequestTreatment.pottingInstructions.fertigationMethod,
        potSubstrate: currentRequestTreatment.pottingInstructions.substrate,
        plantHandlingHumidityInstruction: currentRequestTreatment.plantHandlingInstructions.humidityInstruction,
        plantHandlingLightingInstruction: currentRequestTreatment.plantHandlingInstructions.lightingInstruction,
        plantHandlingTemperatureInstruction: currentRequestTreatment.plantHandlingInstructions.temperatureInstruction,
        comments: st.comments,
        sprayBoothStudyInstructions: currentRequestTreatment.sprayBoothStudyInstructions,
        plannedPlantingDate: this.plannedPlantingDate.value
      });
    });

    this.buildSamplingNeeds();

    this.planningForm.patchValue({
      tagsOnly: this.planningInformation.tagsOnly,
      insectsAllowed: this.planningInformation.insectsAllowed,
      specificAoiRequired: this.request.phytoInspectionRequired ? true : this.planningInformation.specificAoiRequired,
      plannedAoiLocation: this.planningInformation.plannedAoiLocation,
      plannedPlantingDate: this.planningInformation.plannedPlantingDate === null ? this.request.requestedPlantingDate : this.planningInformation.plannedPlantingDate,
      notes: this.planningInformation.notes,
      requestType: this.planningInformation.requestTypeId
    });

    if (this.request.phytoInspectionRequired) {
      this.plannedAoiLocation.setValidators(Validators.required);
    }

    if (this.request.recurrencePattern === RecurrencePattern.Weekly) {
      const validDaysOfWeek = this.getDaysOfWeekRecurrence();
      const plannedPlantingDate = this.planningForm.get('plannedPlantingDate');
      plannedPlantingDate.clearValidators();
      plannedPlantingDate.setValidators([Validators.required, this.validatorsService.mustBeOnOrAfterDate(this.minPlantDate), this.validatorsService.isValidDate, this.validatorsService.allowedDaysOfWeek(validDaysOfWeek)])
      plannedPlantingDate.updateValueAndValidity();
    }

    this.specificAoiRequired.valueChanges.subscribe((value: boolean) => {
      if (value === true || this.request.phytoInspectionRequired) {
        this.plannedAoiLocation.setValidators(Validators.required);
      }
      else {
        this.plannedAoiLocation.clearValidators();
      }
      this.plannedAoiLocation.updateValueAndValidity();
    });

    this.buildRegulationStatusString();
  }

  ngAfterViewInit(): void {
    if (!this.planningTreatmentForms || this.planningTreatmentForms.length === 0) {
      console.warn('planningTreatmentForms is empty. Ensure treatments are rendered in the template.');
    }
  }

  private buildRegulationStatusString() {
    var array: string[] = [];
    if (this.request.ppqRequired)
      array.push("PPQ Required")
    if (this.request.phytoInspectionRequired)
      array.push("Phyto Inspection Required")

    this.regulatoryStatuses = array.join(", ");
  }

  private buildSamplingNeeds() {
    asEnumerable(this.planningInformation.samplingNeeds)
      .OrderBy(n => n.order)
      .ToArray()
      .forEach(n => {
        let laboratory = n.laboratoryId ? asEnumerable(this.labs).SingleOrDefault(l => l.id === n.laboratoryId) : null;
        let labName = laboratory ? laboratory.name : null;
        let assay = n.laboratoryAssayId ? asEnumerable(laboratory.assays).Single(l => l.id === n.laboratoryAssayId).name : null;
        let tissueType = n.tissueTypeId ? asEnumerable(this.tissueTypes).SingleOrDefault(l => l.id === n.tissueTypeId).name : null;

        let tissueCollectionType = TissueCollectionType[n.tissueCollectionType];
        let growthStage = n.growthStageId !== null ? asEnumerable(this.growthStages).SingleOrDefault(l => l.id === n.growthStageId).name : null;

        this.requestedSamplings.push({
          order: n.order,
          laboratory: labName,
          assay: assay,
          tissueType: tissueType,
          tissueCollectionType: tissueCollectionType,
          growthStage: growthStage,
          daysAfterPlanting: n.daysAfterPlanting
        });

        this.requestedSamplingNotes.push(this.formBuilder.control({ value: n.notes, disabled: false }, [Validators.required]));
      });
  }

  private getDaysOfWeekRecurrence = (): number[] => {
    if (this.request.recurrencePattern === RecurrencePattern.None
      || this.request.recurrencePattern === RecurrencePattern.Unknown
      || !this.request.recurrenceDays) {
      return [];
    }

    const daysOfWeekList: RecurrenceDays[] = [RecurrenceDays.Monday, RecurrenceDays.Tuesday, RecurrenceDays.Wednesday, RecurrenceDays.Thursday, RecurrenceDays.Friday];
    const allowedDaysOfWeek: number[] = [];
    const dayPattern = this.request.recurrenceDays;

    for (let i = 0; i < daysOfWeekList.length; i++) {
      if (dayPattern & daysOfWeekList[i]) {
        allowedDaysOfWeek.push(i + 1);
      }
    }

    return allowedDaysOfWeek;
  }

  private buildSaveModel(): PlanningInformation {
    const formValue = this.planningForm.value as PlanningFormModel;
    const newTreatments: PlanningTreatmentDetail[] = [];

    formValue.treatments.forEach((t, index) => {
      const comments: PlanningTreatmentComment[] = [];
      const treatment = this.treatments.at(index);

      const sprayEventData = this.treatmentSprayComponents.toArray()[index].collectSprayEventData();

      if (treatment.get('flatInstructions.irrigation'))
        comments.push(this.createComment(PlanningInformationType.FlatInstruction, PlanningInformationSubtype.Irrigation, t.flatInstructions.irrigation));
      if (treatment.get('flatInstructions.fertigation'))
        comments.push(this.createComment(PlanningInformationType.FlatInstruction, PlanningInformationSubtype.Fertigation, t.flatInstructions.fertigation));
      if (treatment.get('flatInstructions.substrate'))
        comments.push(this.createComment(PlanningInformationType.FlatInstruction, PlanningInformationSubtype.Substrate, t.flatInstructions.substrate));

      if (treatment.get('pottingInstructions.irrigation'))
        comments.push(this.createComment(PlanningInformationType.PottingInstruction, PlanningInformationSubtype.Irrigation, t.pottingInstructions.irrigation));
      if (treatment.get('pottingInstructions.fertigation'))
        comments.push(this.createComment(PlanningInformationType.PottingInstruction, PlanningInformationSubtype.Fertigation, t.pottingInstructions.fertigation));
      if (treatment.get('pottingInstructions.substrate'))
        comments.push(this.createComment(PlanningInformationType.PottingInstruction, PlanningInformationSubtype.Substrate, t.pottingInstructions.substrate));

      if (treatment.get('plantHandlingInstructions.humidity'))
        comments.push(this.createComment(PlanningInformationType.PlantHandlingInstruction, PlanningInformationSubtype.Humidity, t.plantHandlingInstructions.humidity));
      if (treatment.get('plantHandlingInstructions.lighting'))
        comments.push(this.createComment(PlanningInformationType.PlantHandlingInstruction, PlanningInformationSubtype.Lighting, t.plantHandlingInstructions.lighting));
      if (treatment.get('plantHandlingInstructions.temperature'))
        comments.push(this.createComment(PlanningInformationType.PlantHandlingInstruction, PlanningInformationSubtype.Temperature, t.plantHandlingInstructions.temperature));

      newTreatments.push({
        treatmentNumber: t.treatmentNumber,
        flatStorageFacilityTypeId: t.flatInstructions.flatLocation ? t.flatInstructions.flatLocation.facilityType : null,
        flatStorageFacilityLocationId: t.flatInstructions.flatLocation ? t.flatInstructions.flatLocation.facility : null,
        automationPotStorageFacilityTypeId: t.pottingInstructions.automationPotLocation.facilityType,
        automationPotStorageLocationId: t.pottingInstructions.automationPotLocation.facility,
        staticPotStorageFacilityTypeId: t.pottingInstructions.staticPotLocation.facilityType,
        staticPotStorageLocationId: t.pottingInstructions.staticPotLocation.facility,
        comments: comments,
        sprayEventData: sprayEventData,
      })
    });

    const planningInformation: PlanningInformation = {
      id: this.request.id,
      tagsOnly: formValue.tagsOnly,
      insectsAllowed: formValue.insectsAllowed,
      specificAoiRequired: formValue.specificAoiRequired,
      plannedAoiLocation: formValue.plannedAoiLocation,
      notes: formValue.notes,
      requestTypeId: formValue.requestType,
      plannedPlantingDate: formValue.plannedPlantingDate,
      treatmentDetails: newTreatments,
      samplingNeeds: asEnumerable(formValue.requestedSamplingNotes)
        .Select((n, index) => <SamplingNeeds>{
          order: index + 1,
          notes: n
        }).ToArray()
    }

    return planningInformation;
  }

  private createComment(type: PlanningInformationType, subtype: PlanningInformationSubtype, comment: string): PlanningTreatmentComment {
    return {
      instructionType: type,
      instructionSubtype: subtype,
      comment: comment
    }
  }

  public applyAll(sourceComponent: RequestPlanningTreatmentComponent): void {
    const args: PlanningTreatmentCopyArgs = {
      directSeedToPots: sourceComponent.existingTreatmentFormData.directSeedToPots,
      flatInstructions: {
        fertigation: sourceComponent.flatInstructionsFertigation ? sourceComponent.flatInstructionsFertigation.value : null,
        irrigation: sourceComponent.flatInstructionsIrrigation ? sourceComponent.flatInstructionsIrrigation.value : null,
        substrate: sourceComponent.flatInstructionsSubstrate ? sourceComponent.flatInstructionsSubstrate.value : null,
        flatLocation: sourceComponent.flatLocation ? {
          facilityType: sourceComponent.flatFacilityType ? sourceComponent.flatFacilityType.value : null,
          facility: sourceComponent.flatFacility ? sourceComponent.flatFacility.value : null
        } : null
      },
      pottingInstructions: {
        fertigation: sourceComponent.pottingInstructionsFertigation ? sourceComponent.pottingInstructionsFertigation.value : null,
        irrigation: sourceComponent.pottingInstructionsIrrigation ? sourceComponent.pottingInstructionsIrrigation.value : null,
        substrate: sourceComponent.pottingInstructionsSubstrate ? sourceComponent.pottingInstructionsSubstrate.value : null,
        automationPotLocation: {
          facilityType: sourceComponent.automatedFacilityType ? sourceComponent.automatedFacilityType.value : null,
          facility: sourceComponent.automatedFacility ? sourceComponent.automatedFacility.value : null
        },
        staticPotLocation: {
          facilityType: sourceComponent.staticFacilityType ? sourceComponent.staticFacilityType.value : null,
          facility: sourceComponent.staticFacility ? sourceComponent.staticFacility.value : null
        }
      },
      plantHandlingInstructions: {
        lighting: sourceComponent.plantHandlingLighting ? sourceComponent.plantHandlingLighting.value : null,
        humidity: sourceComponent.plantHandlingHumidity ? sourceComponent.plantHandlingHumidity.value : null,
        temperature: sourceComponent.plantHandlingTemperature ? sourceComponent.plantHandlingTemperature.value : null,
      }
    };

    this.planningTreatmentForms.forEach(f => f.copyValueFrom(args));
  }

  public applyAllSpray(
    sourceComponent: RequestPlanningTreatmentSprayComponent
  ): void {
    const args: SprayEventsCopyArgs = {
      sprayEventDaysAfterPlanting: sourceComponent.sprayBoothStudyInstructions
        ? sourceComponent.sprayBoothStudyInstructions.value
        : null,
    };
    this.planningTreatmentForms.forEach((f) => {
      f.copySprayEventsFrom(args);
    });
  }

  public showPlantHandlingInstructions(treatment: FormGroup): boolean {
    let plantHandlingInstructions = treatment.get('plantHandlingInstructions') as FormGroup;

    return (
      plantHandlingInstructions.get('lighting') !== null ||
      plantHandlingInstructions.get('humidity') !== null ||
      plantHandlingInstructions.get('temperature') !== null
    )
  }

  public close(): void {
    this.onClose.next();
  }

  public saveState(): void {
    let model = this.buildSaveModel();
    this.onSave.next(model);
  }

  public completeRequestPlan(): void {
    let model = this.buildSaveModel();
    this.onPlanned.next(model);
  }
}

export interface ExistingTreatmentData {
  treatmentNumber: number;
  directSeedToPots: boolean;
  flatStorageFacilityTypeId: number;
  flatStorageFacilityLocationId: number;
  automationPotStorageFacilityTypeId: number;
  automationPotStorageLocationId: number;
  staticPotStorageFacilityTypeId: number;
  staticPotStorageLocationId: number;
  flatIrrigationMethod: string;
  flatFertigationMethod: string;
  flatSubstrate: string;
  potIrrigationMethod: string;
  potFertigationMethod: string;
  potSubstrate: string;
  plantHandlingHumidityInstruction: string;
  plantHandlingTemperatureInstruction: string;
  plantHandlingLightingInstruction: string;
  comments: PlanningTreatmentComment[];
  sprayBoothStudyInstructions: SprayBoothStudyInstructions[];
  plannedPlantingDate: Date;
}

export interface PlanningFormModel {
  id: number;
  tagsOnly: boolean;
  insectsAllowed: boolean;
  harvestInstructions: string;
  specificAoiRequired: boolean;
  plannedAoiLocation: string;
  plannedPlantingDate: Date;
  notes: string;
  requestType: number;
  treatments: PlanningTreatmentDetailFormModel[];
  requestedSamplingNotes: string[];
}

export interface PlanningTreatmentDetailFormModel {
  treatmentNumber: number;
  flatInstructions: FlatInstructionsFormModel;
  pottingInstructions: PottingInstructionsFormModel;
  plantHandlingInstructions: PlantHandlingInstructionsFormModel;
  sprayBoothStudyInstructions: sprayEventDaysAfterPlantingListModel[];
}

interface FlatInstructionsFormModel {
  flatLocation: FacilitySelectionModel;
  irrigation: string;
  fertigation: string;
  substrate: string;
}

interface PottingInstructionsFormModel {
  automationPotLocation: FacilitySelectionModel;
  staticPotLocation: FacilitySelectionModel;
  irrigation: string;
  fertigation: string;
  substrate: string;
}

interface PlantHandlingInstructionsFormModel {
  humidity: string;
  lighting: string;
  temperature: string;
}

interface FacilitySelectionModel {
  facilityType: number;
  facility: number;
}

interface sprayEventDaysAfterPlantingListModel {
  sprayEventDaysAfterPlanting: number;
}



// WEBPACK FOOTER //
// F:/PHAZP5152IS01-4/_work/202/s/src/app/view-request/request-planning-modal/request-planning-modal.component.ts